import type { ReactElement, ReactNode } from "react";
import { ShieldAlert } from "lucide-react";
import { EmptyState } from "@playbookai/ui";
import { useAuthStore } from "./auth-store.js";
import { canAdministerWorkspace } from "./roles.js";

interface RequireTenantAdminProps {
  /** The surface being gated, e.g. "Workspace settings". Names it in the copy. */
  readonly area: string;
  readonly children: ReactNode;
  readonly title?: string;
  readonly description?: string;
}

/**
 * Route gate for tenant-admin surfaces (PB-48 / AC-06).
 *
 * Mounted in `App.tsx` around each admin-only route, so protection comes from
 * the route wiring rather than from every page remembering to re-implement its
 * own check — and every denial renders the SAME forbidden state. Before this,
 * the old Workspace settings page and KpiSettings each carried a hand-rolled
 * gate with different copy. Since PB-1561 the whole `/admin` tree is gated once
 * at its parent, so its four sections carry no gate of their own at all.
 *
 * UI hiding only; the server stays AUTHORITATIVE (root CLAUDE.md). `services/auth`
 * answers 403 from `TenantAdminGuard` (Auth Postgres `isActiveAdmin`) and
 * playbook-be asserts the `tenant-admin` realm role, so a user who forces their
 * way past this component still gets nothing from the API.
 *
 * Fails closed: `canAdministerWorkspace` treats absent roles as "not an admin"
 * (PB-5 ships `realm_access.roles` on `GET /api/auth/me`).
 */
export function RequireTenantAdmin({
  area,
  children,
  title = "Admin access required",
  description,
}: RequireTenantAdminProps): ReactElement {
  const claims = useAuthStore((s) => s.claims);

  if (!canAdministerWorkspace(claims)) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100svh-180px)] px-6">
        <EmptyState
          variant="error"
          icon={<ShieldAlert className="w-5 h-5" />}
          title={title}
          // Phrased so `area` sits after a verb, not as the subject: a plural
          // area ("Workspace settings") would disagree with "is available".
          description={
            description ??
            `Admins manage ${area}. Ask one if you believe you should have access.`
          }
        />
      </div>
    );
  }

  return <>{children}</>;
}
